import React, { FC, useContext } from 'react';
import { observer } from 'mobx-react';
import { TraceabilityPie } from './TraceabilityPie';
import { TraceabilityLegend } from './TraceabilityLegend';
import { LFNumber } from '@components/LFNumber';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { TraceabilityGraph } from './TraceabilityGraph';
import { TraceabilitySankey } from './TraceabilitySankey';
import { isPresent } from '@farmai-shared/LFUtils';
import { SankeyResponse, StatResponse } from '@services/responseTypes';
export const Traceability: FC<{
  charts?: StatResponse;
  sankeyData?: SankeyResponse;
  legendColors: {
    [k: string]: string;
  };
  crop: string | null;
}> = observer(({
  charts,
  sankeyData,
  legendColors,
  crop
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const weightUnit = userStore.user.settings.weight_unit;
  const cropString = crop || t('all');
  return <>
      <h1 className={`
          block text-3xl
          print:mt-8 print:ml-12 print:hidden
        `}>
        {t('traceability')}
      </h1>
      <div className={`
          keep-together flex w-full flex-col-reverse gap-4
          lg:flex-row
        `}>
        <div className={`
            print:keep-together
            flex w-full flex-col gap-8 rounded-2xl bg-smoke p-4
            lg:w-3/4
          `}>
          <div className={`
              flex h-full flex-col gap-4
              lg:h-144 lg:flex-row
              print:ml-12 print:flex print:justify-center
            `}>
            <div className={`
                keep-together w-full
                print:ml-24
              `}>
              <h1 className={`
                  hidden text-3xl
                  print:my-8 print:block print:text-4xl print:font-bold
                `}>
                {t('traceability')}
              </h1>
              <p className="text-2xl font-light">
                {t('traceabilityPieTitle', {
                crop: cropString
              })}
              </p>
              <TraceabilityPie destinations={charts?.destinations} legendColors={legendColors} />
            </div>
            <div className={`
                border-r-none min-h-max border-spindle
                lg:border-r
              `} />
            <div className={`
                keep-together flex w-full flex-col
                print:mt-64 print:ml-24
              `}>
              <p className="text-2xl font-light">
                {t('traceabilityGraphTitle', {
                crop: cropString,
                unit: t(weightUnit)
              })}
              </p>
              <TraceabilityGraph destinations={charts?.destinations} legendColors={legendColors} />
            </div>
          </div>
          <div className={`
              keep-together h-144 w-full
              print:mt-12 print:ml-24 print:flex-col print:justify-center
            `}>
            <p className={`
                text-2xl font-light
                print:mt-44
              `}>
              {t('traceabilitySankeyTitle', {
              crop: cropString
            })}
            </p>
            <TraceabilitySankey data={sankeyData} legendColors={legendColors} />
          </div>
          <div className={`
              print:keep-together
              hidden h-min w-full flex-col gap-y-8
              lg:w-1/4
              print:mt-36 print:block print:w-full print:break-inside-avoid print:px-24
            `}>
            <div className={`
                flex w-full flex-col gap-y-4 rounded-2xl bg-spindle p-4
                print:ml-4
              `}>
              <p className="text-2xl text-grey">{t('totalSoldProduction')}</p>
              <LFNumber num={isPresent(charts?.destinations) ? parseFloat(charts?.total_sold || '0.00') : null} format="weight" className="text-4xl font-medium" unit={t(userStore.user.settings.weight_unit)} />
            </div>
            <TraceabilityLegend data={sankeyData?.nodes} legendColors={legendColors} />
          </div>
        </div>
        <div className={`
            flex h-min w-full flex-col gap-y-8
            lg:w-1/4
            print:hidden
          `}>
          <div className={`flex w-full flex-col gap-y-4 rounded-2xl bg-spindle p-4`}>
            <p className="text-2xl text-grey">{t('totalSoldProduction')}</p>
            <LFNumber num={isPresent(charts?.destinations) ? parseFloat(charts?.total_sold || '0.00') : null} format="weight" className="text-4xl font-medium" unit={t(userStore.user.settings.weight_unit)} />
          </div>
          <TraceabilityLegend data={sankeyData?.nodes} legendColors={legendColors} />
        </div>
      </div>
    </>;
});